import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import './ApplicationsDetails.styles.scss';
import Button from '@mui/material/Button';
import LoginModal from '../LoginModal/LoginModal.component';
import useTranslation from '../../../translations/use-translation';
import { getFieldConfig } from '../../../constants/serviceFieldConfig';

const highlightButtonSx = (theme) => ({
  textTransform: 'none',
  borderRadius: theme.tokens.radius.xl,
  ':hover': { cursor: 'pointer' },
});

const expandIconSx = { color: 'secondary.main' };

const accordionSummarySx = (theme) => ({
  backgroundColor: 'primary.main',
  minHeight: theme.tokens.spacing[8],
});

const accordionTitleSx = (theme) => ({
  color: 'secondary.main',
  fontSize: theme.tokens.typography.fontSize[20],
});

const accordionDetailsSx = (theme) => ({
  padding: theme.tokens.spacing[24],
  backgroundColor: '#f3f4f9',
});

const ageRelaxationLabelSx = (theme) => ({ marginTop: theme.tokens.spacing[8] });

const getServicesFromStorage = () => {
  try {
    return JSON.parse(localStorage.getItem('cscServices') || '[]');
  } catch {
    return [];
  }
};

const fmtDate = (val, comingSoon) => {
  if (comingSoon) return 'Coming Soon';
  if (!val) return '—';
  // Convert YYYY-MM-DD → DD/MM/YYYY for display
  const parts = val.split('-');
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return val;
};

const selectionCriteriaPhases = [
  { id: '1', phaseName: 'Objective test',        phaseDesc: 'There will be a written exam of 100 optional MCQ.' },
  { id: '2', phaseName: 'Skill Test',             phaseDesc: 'There will be a skill exam kind of practical exam.' },
  { id: '3', phaseName: 'Interview',              phaseDesc: 'There will be a interview exam face to face.' },
  { id: '4', phaseName: 'Document Verification',  phaseDesc: 'There will be a document verification for final qualified candidates.' },
];

const ApplicationDetails = () => {
  const navigate = useNavigate();
  const translate = useTranslation();
  const [open, setOpen] = useState(false);
  const { applicationName } = useParams();

  useEffect(() => { window.scrollTo(0, 0); }, [applicationName]);

  const service = useMemo(() => {
    const name = decodeURIComponent(applicationName || '');
    const all = getServicesFromStorage();
    return all.find((s) => s.serviceName === name) || null;
  }, [applicationName]);

  const isUserLoggedIn = localStorage.getItem('isUserLoggedIn');

  const fieldConfig = getFieldConfig(service?.serviceCategory);
  const hasExamData = fieldConfig.examDates && service && (service.examDate || service.examDateComingSoon || service.admitCardDate || service.admitCardComingSoon);
  const hasDateData = fieldConfig.dates && service && (service.startDate || service.endDate);
  const hasAgeData  = fieldConfig.age && service && (service.minAge || service.maxAge);
  const hasFeeData  = service && (service.feeGeneral !== undefined || service.feeEWS !== undefined || service.feeOBC !== undefined || service.feeScSt !== undefined);

  return (
    <>
      <div className='service-details-page'>
        <div className='highlight'>
          {!isUserLoggedIn ? (
            <>
              <Button className='highlight-button' sx={highlightButtonSx} size='small' variant='outlined' onClick={() => setOpen(true)}>
                {translate('LOGIN')}
              </Button>
              &nbsp;{translate('LOGIN_TEXT_HEADING')}
            </>
          ) : (
            <>
              <Button className='highlight-button' sx={highlightButtonSx} size='small' variant='outlined' onClick={() => navigate('./verify-documents')}>
                {translate('CLICK_HERE')}
              </Button>
              &nbsp;{translate('CLICK_HERE_TEXT_HEADING')}
            </>
          )}
        </div>

        {/* ── Important Dates ── */}
        {(hasDateData || hasExamData) && (
        <div className='accordion-container'>
          <Accordion defaultExpanded>
            <AccordionSummary className='accordion-summary' expandIcon={<ExpandMoreIcon sx={expandIconSx} />} sx={accordionSummarySx}>
              <Typography className='accordion-title' sx={accordionTitleSx}>{translate('ACCORDION_IMP_DATES')}</Typography>
            </AccordionSummary>
            <AccordionDetails className='accordion-details' sx={accordionDetailsSx}>
              {hasDateData && (
                <>
                  <div className='details-row'>
                    <Typography className='details-label'>{translate('ACCORDION_IMP_DATES_START_DATE')}&nbsp;</Typography>
                    <Typography className='details-value'>{fmtDate(service.startDate)}</Typography>
                  </div>
                  <div className='details-row'>
                    <Typography className='details-label'>{translate('ACCORDION_IMP_DATES_END_DATE')}&nbsp;</Typography>
                    <Typography className='details-value details-value-highlight'>{fmtDate(service.endDate)}</Typography>
                  </div>
                </>
              )}
              {hasExamData && (
                <>
                  <div className='details-row'>
                    <Typography className='details-label'>{translate('ACCORDION_IMP_DATES_EXAM')}&nbsp;</Typography>
                    <Typography className='details-value'>{fmtDate(service.examDate, service.examDateComingSoon)}</Typography>
                  </div>
                  <div className='details-row'>
                    <Typography className='details-label'>{translate('ACCORDION_IMP_DATES_ADMIT_CARD')}&nbsp;</Typography>
                    <Typography className='details-value'>{fmtDate(service.admitCardDate, service.admitCardComingSoon)}</Typography>
                  </div>
                </>
              )}
            </AccordionDetails>
          </Accordion>
        </div>
        )}

        {/* ── Age ── */}
        {hasAgeData && (
          <div className='accordion-container'>
            <Accordion defaultExpanded>
              <AccordionSummary className='accordion-summary' expandIcon={<ExpandMoreIcon sx={expandIconSx} />} sx={accordionSummarySx}>
                <Typography className='accordion-title' sx={accordionTitleSx}>{translate('ACCORDION_AGE')}</Typography>
              </AccordionSummary>
              <AccordionDetails className='accordion-details' sx={accordionDetailsSx}>
                {service.minAge && (
                  <div className='details-row'>
                    <Typography className='details-label'>{translate('ACCORDION_AGE_MINIMUM')}&nbsp;</Typography>
                    <Typography className='details-value'>{service.minAge} years</Typography>
                  </div>
                )}
                {service.maxAge && (
                  <div className='details-row'>
                    <Typography className='details-label'>{translate('ACCORDION_AGE_MAXIMUM')}&nbsp;</Typography>
                    <Typography className='details-value'>{service.maxAge} years</Typography>
                  </div>
                )}
                <Typography className='details-label' sx={ageRelaxationLabelSx}>
                  {translate('ACCORDION_AGE_RELAXATION')}
                </Typography>
              </AccordionDetails>
            </Accordion>
          </div>
        )}

        {/* ── Fees ── */}
        {hasFeeData && (
          <div className='accordion-container'>
            <Accordion defaultExpanded>
              <AccordionSummary className='accordion-summary' expandIcon={<ExpandMoreIcon sx={expandIconSx} />} sx={accordionSummarySx}>
                <Typography className='accordion-title' sx={accordionTitleSx}>{translate('ACCORDION_FEE')}</Typography>
              </AccordionSummary>
              <AccordionDetails className='accordion-details' sx={accordionDetailsSx}>
                {[
                  { label: translate('ACCORDION_FEE_GENERAL'), val: service.feeGeneral },
                  { label: translate('ACCORDION_FEE_EWS'),     val: service.feeEWS },
                  { label: translate('ACCORDION_FEE_OBC'),     val: service.feeOBC },
                  { label: translate('ACCORDION_FEE_SC_ST'),   val: service.feeScSt },
                ].map(({ label, val }) =>
                  val !== undefined && val !== '' ? (
                    <div className='details-row' key={label}>
                      <Typography className='details-label'>{label}</Typography>
                      <Typography className='details-value'>&#8377;{val}</Typography>
                    </div>
                  ) : null
                )}
              </AccordionDetails>
            </Accordion>
          </div>
        )}

        {/* ── Selection Criteria (shown for exam-type services) ── */}
        {hasExamData && (
          <div className='accordion-container'>
            <Accordion defaultExpanded>
              <AccordionSummary className='accordion-summary' expandIcon={<ExpandMoreIcon sx={expandIconSx} />} sx={accordionSummarySx}>
                <Typography className='accordion-title' sx={accordionTitleSx}>{translate('ACCORDION_SELECTION_CRITERIA')}</Typography>
              </AccordionSummary>
              <AccordionDetails className='accordion-details' sx={accordionDetailsSx}>
                <Typography className='selection-criteria-description'>
                  {translate('ACCORDION_SELECTION_CRITERIA_DESC')}
                </Typography>
                {selectionCriteriaPhases.map((item, index) => (
                  <div className='criteria-item' key={item.id}>
                    <Typography className='criteria-phase'>Phase {index + 1}: {item.phaseName}</Typography>
                    <Typography className='criteria-description'>Description: {item.phaseDesc}</Typography>
                  </div>
                ))}
              </AccordionDetails>
            </Accordion>
          </div>
        )}

        {/* ── Official Links ── */}
        {(service?.notificationFile || service?.officialLink) && (
          <div className='accordion-container'>
            <Accordion defaultExpanded>
              <AccordionSummary className='accordion-summary' expandIcon={<ExpandMoreIcon sx={expandIconSx} />} sx={accordionSummarySx}>
                <Typography className='accordion-title' sx={accordionTitleSx}>{translate('ACCORDION_OFFICIAL_LINKS')}</Typography>
              </AccordionSummary>
              <AccordionDetails className='accordion-details' sx={accordionDetailsSx}>
                <div className='details-row'>
                  <Typography className='details-label'>{translate('ACCORDION_OFFICIAL_LINKS_NOTIFICATION')}&nbsp;</Typography>
                  <Typography className='details-value'>
                    {service.notificationFile
                      ? <a href={service.notificationFile} download={service.notificationFileName || 'notification.pdf'}>Download</a>
                      : <a href={service.officialLink} target='_blank' rel='noreferrer'>Download</a>
                    }
                  </Typography>
                </div>
                <div className='details-row'>
                  <Typography className='details-label'>{translate('ACCORDION_OFFICIAL_LINKS_WEBSITE')}&nbsp;</Typography>
                  <Typography className='details-value'>
                    <a href={service.officialLink} target='_blank' rel='noreferrer'>{service.officialLink}</a>
                  </Typography>
                </div>
              </AccordionDetails>
            </Accordion>
          </div>
        )}
      </div>

      {open && <LoginModal openLoginModal={open} setOpenLoginModal={setOpen} />}
    </>
  );
};

export default ApplicationDetails;
